// Owner 2026-10-29: the "Recently Visited" card on the home page stayed empty.
// Nothing recorded a visit any more (the card only re-recorded clicks on its own
// rows), so it could never fill. One tracker now runs on every route change in the
// authenticated layout (VerticalLayout): the last 5 distinct pages the person
// opened, anywhere in the app, labelled from the workflow catalog or the route
// title. Kept in localStorage for the instant paint and roamed through the user
// preference "recents" so it follows the person to another browser or PC.
import { WORKFLOWS } from "../Components/Common/MenuHub";
import { routeMeta } from "../utils/routeMeta";
import { getPageMeta } from "../utils/getPageMeta";
import { savePref } from "./prefs_api";

export const RECENTS_KEY = "sswHome:recents";
export const RECENTS_EVENT = "ssw:recentsChanged";
export const MAX_RECENTS = 5;

// Pages that are not worth listing: the home page itself, sign-in, the menu hub.
const SKIP_EXACT = new Set(["/", "/dashboard", "/index", "/workflows", "/access-denied", "/login", "/logout"]);
const SKIP_PREFIX = ["/auth", "/login", "/logout", "/ApproveDMS", "/display-boards"];

export function readRecents() {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
}

function writeRecents(list, { roam = true } = {}) {
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list)); } catch { /* storage blocked */ }
    if (roam) savePref("recents", JSON.stringify(list));
    try { window.dispatchEvent(new CustomEvent(RECENTS_EVENT, { detail: list })); } catch { /* old browser */ }
}

// Every catalog entry (workflow + its sub-actions) flattened to {to, label, icon}.
let catalog = null;
function catalogEntries() {
    if (catalog) return catalog;
    const out = [];
    for (const w of WORKFLOWS || []) {
        if (w?.to && w.label) out.push({ to: w.to, label: w.label, icon: w.icon });
        for (const s of w?.subActions || []) {
            if (s?.to && s.label) out.push({ to: s.to, label: s.label, icon: s.icon || w.icon });
        }
    }
    // longest path first so "/epo/shipping" beats "/epo"
    out.sort((a, b) => b.to.length - a.to.length);
    catalog = out;
    return out;
}

const pathOf = (to) => (to || "").split("?")[0].split("#")[0];

/** Label and icon for a path: exact catalog match, route title, catalog prefix, then the path itself. */
export function describePath(pathname) {
    const path = pathOf(pathname);
    const entries = catalogEntries();
    const exact = entries.find((e) => pathOf(e.to) === path);
    if (exact) return { label: exact.label, icon: exact.icon };
    const meta = getPageMeta(path, routeMeta);
    const prefix = entries.find((e) => path.startsWith(pathOf(e.to) + "/") || path === pathOf(e.to));
    if (meta?.title) return { label: meta.title, icon: prefix?.icon || "ri-arrow-right-line" };
    if (prefix) return { label: prefix.label, icon: prefix.icon };
    const tail = path.split("/").filter(Boolean).pop() || path;
    return { label: tail.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), icon: "ri-arrow-right-line" };
}

export function shouldTrack(pathname) {
    const path = pathOf(pathname);
    if (!path || SKIP_EXACT.has(path)) return false;
    return !SKIP_PREFIX.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"));
}

/** Record that the person opened a page. Newest first, one row per path, five rows. */
export function recordVisit(pathname, override = {}) {
    const path = pathOf(pathname);
    if (!shouldTrack(path)) return;
    const d = describePath(path);
    const entry = { to: path, label: override.label || d.label, icon: override.icon || d.icon, ts: Date.now() };
    const list = [entry, ...readRecents().filter((r) => pathOf(r.to) !== path)].slice(0, MAX_RECENTS);
    writeRecents(list);
}

export function clearRecents() {
    writeRecents([]);
}
